import * as React from "react";

import type { TabListProps } from "./TabList.types";
import type { SelectTabEventHandler } from "@fluentui/react-components";
import { ArrowLeft, ArrowRight } from "@fluentui/keyboard-keys";
import { getRTL } from "@fluentui/react/lib/Utilities";

export const useOnKeyDown: (
  props: TabListProps,
  currentSelectedId: string,
  setSelectedId: React.Dispatch<React.SetStateAction<string>>,
  setFocusOnOverflow: React.Dispatch<React.SetStateAction<boolean>>,
  onTabSelect: SelectTabEventHandler,
  focusOnOverflow: boolean
) => React.KeyboardEventHandler<HTMLDivElement> = (
  props,
  currentSelectedId,
  setSelectedId,
  setFocusOnOverflow,
  onTabSelect,
  focusOnOverflow
) => {
  const { onKeyDown, topLevelTabHeaders, overflowTabHeaders } = props;

  return React.useCallback(
    (event) => {
      const isRTL = getRTL();
      const hasOverflow = overflowTabHeaders && overflowTabHeaders.length > 0;
      // create an array of the tabs to navigate
      const tabsArray = Array.from(topLevelTabHeaders).map(
        (header) => header.id
      );
      const tabsArrayLength = tabsArray.length;

      // Find the current index, needed incase of resize
      let currentIndex = focusOnOverflow ? tabsArrayLength : 0;
      if (!focusOnOverflow) {
        currentIndex = tabsArray.findIndex(
          (element: string) => element === currentSelectedId
        );
      }
      if (currentIndex < 0) {
        currentIndex = 0;
      }

      // see if we are increasing across the tabs
      if (
        (!isRTL && event.key === ArrowRight) ||
        (isRTL && event.key === ArrowLeft)
      ) {
        const localIndex = currentIndex + 1;
        // First account for the case where focus on the overflow
        // and we are moving off of it. Or if we are at end of tabs.
        // We need to ensure we set focusOnOverflow back to false
        if (
          focusOnOverflow ||
          (localIndex === tabsArrayLength && !hasOverflow)
        ) {
          const id = tabsArray[0];
          // fire the tab select event to select the new tab
          onTabSelect(event, { value: id });
          // update the selected ID and current index in the state
          setSelectedId(id);
          setFocusOnOverflow(false);
        }
        // if we have overflow and are at end of tabs, focus on on overflow update index only
        else if (localIndex === tabsArrayLength && hasOverflow) {
          // do nothing, focus is on the overflow
          setFocusOnOverflow(true);
        }
        // lastly if we are just navigating tabs, switch to the next one
        else {
          const id = tabsArray[localIndex];
          // fire the tab select event to select the new tab
          onTabSelect(event, { value: id });
          // update the selected ID and current index in the state
          setSelectedId(id);
        }
      }
      // see if we are decreasing across the tabs
      else if (
        (!isRTL && event.key === ArrowLeft) ||
        (isRTL && event.key === ArrowRight)
      ) {
        const localIndex = currentIndex - 1;
        // First account for the case where focus on the overflow
        // and we are moving off of it. We need to ensure we set
        // focusOnOverflow back to false. OR check if we are looping
        // back from the first tab to the last tab when there is no overflow
        if (focusOnOverflow || (localIndex < 0 && !hasOverflow)) {
          const id = tabsArray[tabsArrayLength - 1];
          // fire the tab select event to select the new tab
          onTabSelect(event, { value: id });
          // update the selected ID and current index in the state
          setSelectedId(id);
          setFocusOnOverflow(false);
        }
        // check if we are looping back from the
        // first tab to the overflow, tab selection remains
        // on the first tab but focus moves
        else if (localIndex < 0 && hasOverflow) {
          // do nothing, focus is on the overflow
          setFocusOnOverflow(true);
        }
        // lastly if we are just navigating tabs, switch to the next one
        else {
          const id = tabsArray[localIndex];
          // fire the tab select event to select the new tab
          onTabSelect(event, { value: id });
          // update the selected ID and current index in the state
          setSelectedId(id);
          setFocusOnOverflow(false);
        }
      }
      // bubble up the onkeydownevent if needed
      if (onKeyDown) {
        onKeyDown(event);
      }
    },
    [
      onKeyDown,
      setFocusOnOverflow,
      topLevelTabHeaders,
      onTabSelect,
      overflowTabHeaders,
      focusOnOverflow,
      currentSelectedId,
      setSelectedId,
    ]
  );
};
